import type { TaskExecution } from 'entities/workflow-execution';
import type { SigneExecutionTask } from 'features/document-signe/workflow/model/signe-task';
import { useMemo } from 'react';
import type { SignatureCode } from 'shared/signatures';
import type { GetDocumentCollectionRequestType, Queue } from '../types';
import { useGetDocumentQueueCollection } from './use-get-document-queue-collection';

const calculateStatus = (props: {
  active: boolean;
  rejectedAt?: string | null;
  approvedAt?: string | null;
}) => {
  if (props.rejectedAt || props.approvedAt) {
    return 'completed';
  }

  if (props.active) {
    return 'waiting';
  }

  return 'pending';
};

const convertQueueToTaskExecution = (
  documentId: number,
  queue: Queue,
): TaskExecution<any, any>[] => {
  const result: TaskExecution<any>[] = [];

  queue.acceptors.forEach(_acceptor => {});

  queue.signers.forEach((signer, stepIndex) => {
    signer.queue.forEach((queueItem, queueItemIndex) => {
      const signSigneExecutionTask: SigneExecutionTask = {
        id: `signe-${documentId}`,
        task: {
          id: `signe-${stepIndex}-${queueItemIndex}`,
          status: calculateStatus({
            active: queueItem.active,
            rejectedAt: queueItem.rejectedAt,
            approvedAt: queueItem.approvedAt,
          }),
          workflow: {
            id: -1,
            name: '',
          },
          current: {
            nodeId: `node-signe-${stepIndex}-${queueItemIndex}`,
            nodeType: 'signe-old-queue' as any,
            attributes: {
              documents: [],
              availableMethods:
                queueItem.availableSignMethods as SignatureCode[],
              user: queueItem.user,
            },
          },
          meta: {
            userId: queueItem.user.id,
            documentUserId: queueItem.id,
          },
        },
        errors: [],
        data: {
          signatureType: queueItem.signMethod,
          signatures: [],
        },
      };

      result.push(signSigneExecutionTask);
    });
  });

  return result;
};

/**
 * Custom hook to fetch a collection of tasks documents by their IDs.
 *
 * @param documentIds - The IDs of the documents to fetch.
 */
export const useGetDocumentsTasksCollection = (
  documentIds?: GetDocumentCollectionRequestType['documentIds'],
) => {
  const queueQuery = useGetDocumentQueueCollection(documentIds);

  const data = useMemo(
    () =>
      queueQuery.data?.map(queueItem => {
        if (queueItem.error || !queueItem.data) {
          return queueItem;
        }

        const tasks = convertQueueToTaskExecution(
          queueItem.documentId,
          queueItem.data.data as any,
        );

        return {
          documentId: queueItem.documentId,
          data: tasks,
        };
      }),
    [queueQuery.data],
  ) as any[];

  return {
    data,
    isLoading: queueQuery.isLoading,
    isInitialLoading: queueQuery.isInitialLoading,
  };
};
